<script setup>

import notification from "@/utils/plugins/NotificationService";
import { computed, onMounted, ref } from 'vue';
import { useUiStore } from "@/app/stores/uiStore";
import { filterByNormalizedText } from "@/utils/textSearch";
import ConfirmMaterialDialog from './ConfirmMaterialDialog.vue';
import RequestMaterialReturn from './RequestMaterialReturn.vue';
import MaterialInfoFields from './MaterialInfoFields.vue';


// Services
import EmployeeService from '@/api/services/employee.service';
import MaterialService from '@/api/services/material.service';
import materialAssignmentService from '@/api/services/materialAssignment.service';
import MaterialTypeService from '@/api/services/materialType.service';

const employees = ref([]);
const employeeSearchQuery = ref('');
const responsibles = ref([
    {
        name: "Empleados",
        value: "employee",
    },
    {
        name: "Sistemas",
        value: "systems"
    },
]);
const allMaterials = ref([])
const newMaterial = ref({})
const params = ref({});

const systemReturnDate = ref();
const systemReturnReason = ref('');
const returnedSystemMaterial = ref();
// eslint-disable-next-line no-undef
const emit = defineEmits(['closeDialog'])

const employeeAction = ref(null);
const canConfirmDelivery = computed(() => props.employee && !!props.material &&
    !props.material.confirmedByEmployeeAt && !props.material.returnedAt && !props.material.receivedBySystemsAt);
const canRequestReturn = computed(() => props.employee && !!props.material?.confirmedByEmployeeAt &&
    !props.material.returnedAt && !props.material.receivedBySystemsAt);

const openEmployeeAction = (action) => {
    if ((action === 'confirm' && canConfirmDelivery.value) ||
        (action === 'return' && canRequestReturn.value)) {
        employeeAction.value = action;
    }
};

const closeEmployeeAction = (saved = false) => {
    employeeAction.value = null;
    emit('closeDialog', saved === true);
};

const materialTypes = ref([]);
const selectedType = ref();

// eslint-disable-next-line no-undef
const props = defineProps({
    material: Object,
    employee: Boolean,
})

const filteredMaterial = computed(() => {
    if (!selectedType.value) {
        if (newMaterial.value.responsible === 'systems') {
            return allMaterials.value.filter(mat => mat.ownerType === 'systems');
        }
        if (newMaterial.value.responsible === 'employee') {
            return allMaterials.value.filter(mat => mat.ownerType === 'employee');
        }
        return allMaterials.value;
    }
    let filtered = allMaterials.value.filter(mat => mat.type['@id'] === selectedType.value);
    if (newMaterial.value.responsible === 'systems') {
        filtered = filtered.filter(mat => mat.ownerType === 'systems');
    } else if (newMaterial.value.responsible === 'employee') {
        filtered = filtered.filter(mat => mat.ownerType === 'employee');
    }
    return filtered;
});
const filteredEmployees = computed(() => {
    return filterByNormalizedText(employees.value, employeeSearchQuery.value, employee => employee.fullName);
});

const selectedMaterial = computed(() => {
    if (props && props.material) {
        return props.material;
    } else {
        return null;
    }
})

const isAssignmentConfirmed = computed(() => {
    return !!newMaterial.value.confirmedByEmployeeAt;
})

const canCompleteReturnBySystems = computed(() => {
    return !!selectedMaterial.value
        && !props.employee
        && isAssignmentConfirmed.value
        && !newMaterial.value.returnedAt
        && !newMaterial.value.receivedBySystemsAt;
})

const canReceiveReturn = computed(() => {
    return !!selectedMaterial.value
        && !props.employee
        && isAssignmentConfirmed.value
        && !!newMaterial.value.returnedAt
        && !newMaterial.value.receivedBySystemsAt;
})

const uiStore = useUiStore();
const pad = (n) => { return String(Math.floor(Math.abs(n))).padStart(2, '0'); }
const toISOStringWithOffset = (date = new Date()) => {
    const tzOffsetMin = -date.getTimezoneOffset();
    const sign = tzOffsetMin >= 0 ? '+' : '-';
    const offset = `${sign}${pad(tzOffsetMin / 60)}:${pad(tzOffsetMin % 60)}`;

    return date.getFullYear() +
        '-' + pad(date.getMonth() + 1) +
        '-' + pad(date.getDate()) +
        'T' + pad(date.getHours()) +
        ':' + pad(date.getMinutes()) +
        ':' + pad(date.getSeconds()) +
        offset;
}

onMounted(() => {
    systemReturnDate.value = toISOStringWithOffset()
    returnedSystemMaterial.value = toISOStringWithOffset()
    newMaterial.value.assignedAt = toISOStringWithOffset()
    if (selectedMaterial.value != null) {
        newMaterial.value.employee = selectedMaterial.value.employee;
        newMaterial.value.material = selectedMaterial.value.material;
        selectedType.value = selectedMaterial.value.material.type['@id'];
        newMaterial.value.assignedAt = selectedMaterial.value.assignedAt;
        newMaterial.value.confirmedByEmployeeAt = selectedMaterial.value.confirmedByEmployeeAt;
        newMaterial.value.returnedAt = selectedMaterial.value.returnedAt;
        newMaterial.value.returnReason = selectedMaterial.value.returnReason;
        newMaterial.value.receivedBySystemsAt = selectedMaterial.value.receivedBySystemsAt;
        newMaterial.value.returnedTo = selectedMaterial.value.returnedTo;
        newMaterial.value.responsible = selectedMaterial.value.responsible;
    }
    if (!newMaterial.value.responsible) {
        newMaterial.value.responsible = 'systems'
    }
    if (selectedMaterial.value === null) {
        getEmployees();
        // getMaterials();
    } else {
        allMaterials.value.push(selectedMaterial.value.material)
        employees.value.push(selectedMaterial.value.employee)
    }
    getMaterialsTypes();
})

const closeDialog = (saved = null) => {
    if (saved) {
        emit('closeDialog', saved)
    } else {
        emit('closeDialog')
    }
}

// Getters

const getEmployees = (filters) => {

    params.value = {
        ...params.value,
        filter: filters,
        active: true,
        'order[name]': 'asc',
        page: 1,
        itemsPerPage: 999999,
    };
    uiStore.startLoading();
    EmployeeService.getEmployees(params.value)
        .then((res) => {
            employees.value = res.data['hydra:member'];
            uiStore.stopLoading();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}
const filterEmployeesByName = (query) => {
    employeeSearchQuery.value = query;
}

const getMaterials = () => {
    const params = {
        page: 1,
        itemsPerPage: 99999,
    }
    if (selectedMaterial.value === null) {
        params.status = "available"
    }
    if (selectedType.value) {
        params.type = selectedType.value.id;
    }

    uiStore.startLoading();
    MaterialService.getMaterials(params)
        .then((res) => {
            if (res.status === 200) {
                allMaterials.value = res.data['hydra:member']
            }
            uiStore.stopLoading();
        })
}

const getMaterialsTypes = () => {
    // eslint-disable-next-line no-unused-vars
    const param = {
        'page': 1,
        'itemsPerPage': 99999,
    }
    uiStore.startLoading();
    MaterialTypeService.getMaterialTypes(param)
        .then((res) => {
            if (res.status === 200) {
                materialTypes.value = res.data['hydra:member']
            }

            uiStore.stopLoading();
        })
}

// Changes
const changeMaterial = () => {
    if (newMaterial.value && newMaterial.value.material && newMaterial.value.material.type) {
        selectedType.value = newMaterial.value.material.type['@id'];
    } else {
        selectedType.value = null;
    }
}

const changeResponsible = () => {
    if (!newMaterial.value.material) return;
    const list = filteredMaterial.value;
    const exist = list.some(mat => {
        return mat.id === newMaterial.value.material.id;
    })
    if (!exist) {
        newMaterial.value.material = null;
        selectedType.value = null;
    }
}

const changeType = () => {

    getMaterials();
    // if (!newMaterial.value.material) return;
    // const list = filteredMaterial.value;
    // const exist = list.some(mat => {
    //     return mat.id === newMaterial.value.material.id;
    // })
    // if (!exist) newMaterial.value.material = null;
}

const saveEntry = () => {
    if (newMaterial.value && !newMaterial.value.responsible) {
        notification.addNotification({
            type: 'error',
            message: 'Tienes que elegir un responsable',
            duration: 6000
        })
        return;
    }
    if (selectedMaterial.value) {
        updateMaterialDelivery()
    } else {
        createNewMaterialDelivery()
    }
}

const createNewMaterialDelivery = () => {
    const params = { ...newMaterial.value };
    if (!newMaterial.value || !newMaterial.value.material) {
        notification.addNotification({
            type: 'error',
            message: 'Tienes que rellenar el formulario',
            duration: 6000
        })
        return;
    }
    if (newMaterial.value.responsible !== 'systems' && !newMaterial.value.employee) {
        notification.addNotification({
            type: 'error',
            message: 'Tienes que rellenar el formulario',
            duration: 6000
        })
        return;
    }
    if (newMaterial.value.responsible !== 'systems') {
        params.employee = newMaterial.value.employee['@id']
    }
    params.material = newMaterial.value.material['@id']

    materialAssignmentService.createMaterial(params)
        .then((res) => {
            if (res && (res.status === 200 || res.status === 201)) {
                notification.addNotification({
                    type: 'success',
                    message: 'Creado el material correctamente',
                    duration: 6000
                })
                closeDialog(true);
            }
        })
}

const updateMaterialDelivery = () => {

    const id = selectedMaterial.value.id;
    const params = { ...newMaterial.value };
    params.employee = newMaterial.value.employee['@id']
    params.material = newMaterial.value.material['@id']

    uiStore.startLoading();
    materialAssignmentService.updateMaterial(id, params)
        .then((res) => {
            if (res && res.status === 200) {
                notification.addNotification({
                    type: 'success',
                    message: 'Se ha actualizado el material correctamente',
                    duration: 6000
                })
                closeDialog(true);
            }
            uiStore.stopLoading();
        })
}

const completeReturnBySystems = () => {
    if (!isAssignmentConfirmed.value) {
        notification.addNotification({
            type: 'error',
            message: 'No se puede completar la devolución sin la asignación confirmada',
            duration: 6000
        })
        return;
    }

    if (!systemReturnDate.value) {
        notification.addNotification({
            type: 'error',
            message: 'Inserte una fecha de devolución',
            duration: 6000
        })
        return;
    }

    if (!systemReturnReason.value || !systemReturnReason.value.trim()) {
        notification.addNotification({
            type: 'error',
            message: 'Inserte una razón de devolución',
            duration: 6000
        })
        return;
    }

    if (!selectedMaterial.value) {
        notification.addNotification({
            type: 'error',
            message: 'No se ha encontrado material recibido',
            duration: 6000
        })
        return;
    }

    params.value = {
        returnedAt: systemReturnDate.value,
        returnReason: systemReturnReason.value.trim(),
    };

    uiStore.startLoading();
    materialAssignmentService.completeReturnBySystems(selectedMaterial.value.id, params.value)
        .then((res) => {
            if (res && (res.status === 200 || res.status === 201)) {
                notification.addNotification({
                    type: 'success',
                    message: 'Devolución completada correctamente',
                    duration: 6000
                })
                closeDialog(true);
            }
            uiStore.stopLoading();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}

const receiveMaterial = () => {
    if (!isAssignmentConfirmed.value) {
        notification.addNotification({
            type: 'error',
            message: 'No se puede confirmar la devolución sin la asignación confirmada',
            duration: 6000
        })
        return;
    }

    if (!returnedSystemMaterial.value) {
        notification.addNotification({
            type: 'error',
            message: 'Inserte una fecha en la recepción de la devolución',
            duration: 6000
        })
        return;
    }

    if (!selectedMaterial.value) {
        notification.addNotification({
            type: 'error',
            message: 'No se ha encontrado material recibido',
            duration: 6000
        })
        return;
    }

    params.value = {
        receivedBySystemsAt: returnedSystemMaterial.value
    };
    uiStore.startLoading();
    materialAssignmentService.recieveMaterialEmployee(selectedMaterial.value.id, params.value)
        .then((res) => {
            if (res && (res.status === 200 || res.status === 201)) {
                notification.addNotification({
                    type: 'success',
                    message: 'Se ha recibido la devolución correctamente',
                    duration: 6000
                })
                closeDialog(true);
            }
            uiStore.stopLoading();
        })
        .catch(() => {
            uiStore.stopLoading();
        })
}

</script>

<template>
    <ConfirmMaterialDialog v-if="employeeAction === 'confirm'" :material="material"
        @close-dialog="closeEmployeeAction" />
    <RequestMaterialReturn v-else-if="employeeAction === 'return'" :material="material"
        @close-dialog="closeEmployeeAction" />
    <div v-else>
        <div class="header">
            <svg xmlns="http://www.w3.org/2000/svg" width="37" height="37" viewBox="0 0 37 37" fill="none">
                <g clip-path="url(#clip0_1945_3368)">
                    <path
                        d="M16.65 32.338L7.4 27.01C6.808 26.677 6.364 26.2145 6.031 25.6595C5.698 25.1045 5.55 24.494 5.55 23.8095V13.172C5.55 12.4875 5.7165 11.877 6.031 11.322C6.3455 10.767 6.808 10.323 7.4 9.9715L16.65 4.6435C17.242 4.3105 17.8525 4.1255 18.5 4.1255C19.1475 4.1255 19.758 4.292 20.35 4.6435L29.6 9.9715C30.192 10.3045 30.636 10.767 30.969 11.322C31.302 11.877 31.45 12.4875 31.45 13.172V23.8095C31.45 24.494 31.2835 25.1045 30.969 25.6595C30.6545 26.2145 30.192 26.6585 29.6 27.01L20.35 32.338C19.758 32.671 19.1475 32.856 18.5 32.856C17.8525 32.856 17.242 32.6895 16.65 32.338ZM16.65 28.083V19.573L9.25 15.2625V23.828L16.65 28.083ZM20.35 28.083L27.75 23.828V15.2625L20.35 19.573V28.083ZM0 9.25V3.7C0 2.6825 0.37 1.813 1.0915 1.0915C1.813 0.37 2.6825 0 3.7 0H9.25V3.7H3.7V9.25H0ZM9.25 37H3.7C2.6825 37 1.813 36.63 1.0915 35.9085C0.37 35.187 0 34.3175 0 33.3V27.75H3.7V33.3H9.25V37ZM27.75 37V33.3H33.3V27.75H37V33.3C37 34.3175 36.63 35.187 35.9085 35.9085C35.187 36.63 34.3175 37 33.3 37H27.75ZM33.3 9.25V3.7H27.75V0H33.3C34.3175 0 35.187 0.37 35.9085 1.0915C36.63 1.813 37 2.6825 37 3.7V9.25H33.3ZM18.5 16.3725L25.8075 12.062L18.5 7.844L11.1925 12.062L18.5 16.3725Z"
                        fill="#474CE6" />
                </g>
                <defs>
                    <clipPath id="clip0_1945_3368">
                        <rect width="37" height="37" fill="white" />
                    </clipPath>
                </defs>
            </svg>

            <span class="header-title pl-4">
                Entrega de Material
            </span>
        </div>
        <div class="row f-flex">
            <div class="col-6 el-col-xs-24">
                <div class="form-item date-picker mt-4 w-100 label-common pl-5">
                    <label for="ffrom" class="w-100">Fecha de entrega</label>
                    <el-date-picker popper-class="material-modal-picker" id="ffrom" v-model="newMaterial.assignedAt" name="from-dat'" type="datetime"
                        class="w-100" placeholder="Fecha entrega" format="DD/MM/YYYY HH:mm"
                        value-format="YYYY-MM-DD HH:mm" :disabled="employee" :picker-options="{ firstDayOfWeek: 1 }" />
                </div>
            </div>
            <div class="col-6 el-col-xs-24" v-if="newMaterial.confirmedByEmployeeAt">
                <div class="form-item mt-4 w-100 label-common pl-5">
                    <label for="ffto" class="w-100">Fecha de confirmación de entrega</label>
                    <el-date-picker popper-class="material-modal-picker" id="ffto" v-model="newMaterial.confirmedByEmployeeAt" name="to-dat'" type="datetime"
                        class="w-100" :disabled="employee" placeholder="Fecha recepción" format="DD/MM/YYYY HH:mm"
                        value-format="YYYY-MM-DD HH:mm" :default-value="new Date()"
                        :picker-options="{ firstDayOfWeek: 1 }" />
                </div>
            </div>
            <div class="col-6 el-col-xs-24" v-if="newMaterial.returnedAt">
                <div class="form-item date-picker mt-4 w-100 label-common pl-5">
                    <label for="fret" class="w-100">Fecha de solicitud de devolución</label>
                    <el-date-picker popper-class="material-modal-picker" id="fret" v-model="newMaterial.returnedAt" name="to-dat'" type="datetime"
                        class="w-100" :disabled="employee" placeholder="Fecha recepción" format="DD/MM/YYYY HH:mm"
                        value-format="YYYY-MM-DD HH:mm" :default-value="new Date()"
                        :picker-options="{ firstDayOfWeek: 1 }" />
                </div>
            </div>

            <div class="col-12">
                <div class="row f-flex">
                    <div class="col-6 el-col-xs-24" v-if="!employee">
                        <div class="form-item mt-4 w-100 label-common pl-5">
                            <label class="w-100">Responsable</label>
                            <el-select popper-class="material-modal-select" v-model="newMaterial.responsible" class="w-100" filterable
                                placeholder="Seleccione un responsable" :value-key="'@id'" @change="changeResponsible"
                                :disabled="(selectedMaterial != null && selectedMaterial.employee != null) || employee">
                                <el-option v-for="responsible in responsibles" :key="responsible.value"
                                    :label="responsible.name" :value="responsible.value">
                                </el-option>
                            </el-select>
                        </div>
                    </div>
                    <div class="col-6 el-col-xs-24"
                        v-if="!employee && newMaterial.responsible && newMaterial.responsible !== 'systems'">
                        <div class="form-item mt-4 w-100 label-common pl-5">
                            <label class="w-100">Empleado al que se entrega</label>
                            <el-select popper-class="material-modal-select" v-model="newMaterial.employee" class="w-100" filterable
                                placeholder="Seleccione un empleado" :filter-method="filterEmployeesByName"
                                :value-key="'@id'"
                                :disabled="(selectedMaterial != null && selectedMaterial.employee != null) || employee">
                                <el-option v-for="employee in filteredEmployees" :key="employee.id"
                                    :label="employee.fullName" :value="employee">
                                </el-option>
                            </el-select>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12">
                <div class="row f-flex">
                    <div class="col-6 el-col-xs-24" v-if="newMaterial.receivedBySystemsAt">
                        <div class="form-item mt-4 w-100 label-common pl-5">
                            <label for="ffrom" class="w-100">Fecha de recepción de la devolución</label>
                            <el-date-picker popper-class="material-modal-picker" id="fto" v-model="newMaterial.receivedBySystemsAt" name="to-dat'"
                                type="datetime" class="w-100" disabled placeholder="Fecha recepción"
                                format="DD/MM/YYYY HH:mm" value-format="YYYY-MM-DD HH:mm" :default-value="new Date()"
                                :picker-options="{ firstDayOfWeek: 1 }" />
                        </div>
                    </div>
                    <div class="col-6 el-col-xs-24"
                        v-if="newMaterial.returnedTo != null || newMaterial.returnedTo != undefined">
                        <div class="form-item mt-4 w-100 label-common pl-5">
                            <label class="w-100">Recogido por</label>
                            <el-input type="text" v-model="newMaterial.returnedTo.fullName" disabled />
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 material-selector-group">
                <div class="row f-flex material-selector-row">
                    <div class="col-6 el-col-xs-24">
                        <div class="form-item mt-4 w-100 label-common pl-5">
                            <label class="w-100">Tipo de material</label>
                            <el-select popper-class="material-modal-select" v-model="selectedType" class="w-100" filterable
                                :disabled="(selectedMaterial != null && selectedMaterial.material != null) || employee"
                                @change="changeType" clearable placeholder="Seleccione tipo de material"
                                :value-key="'@id'">
                                <el-option v-for="material in materialTypes" :key="material['@id']"
                                    :label="material.name" :value="material['@id']">
                                </el-option>
                            </el-select>
                        </div>
                    </div>
                    <div class="col-6 el-col-xs-24">
                        <div class="form-item mt-4 w-100 label-common pl-5">
                            <label class="w-100">Número de serie</label>
                            <el-select popper-class="material-modal-select" v-model="newMaterial.material" class="w-100" filterable
                                :disabled="(selectedMaterial != null && selectedMaterial.material != null) || employee"
                                clearable placeholder="Seleccione numero de serie del material" :value-key="'@id'"
                                @change="changeMaterial">
                                <el-option v-for="material in filteredMaterial" :key="material.id"
                                    :label="material.serialTag" :value="material">
                                </el-option>
                            </el-select>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12">
                <MaterialInfoFields :material="newMaterial.material" :show-identification="false" />
            </div>
            <div class="col-12 mt-3" v-if="canCompleteReturnBySystems">
                <div>
                    <h3>Completar devolución</h3>
                    <div class="row f-flex">
                        <div class="col-md-7 el-col-xs-24">
                            <div class="form-item date-picker w-100 label-common pl-5">
                                <label for="fsystemReturn" class="w-100">Fecha de devolución</label>
                                <el-date-picker popper-class="material-modal-picker" id="fsystemReturn" v-model="systemReturnDate" name="return-date"
                                    class="w-100" type="datetime" placeholder="Fecha de devolución"
                                    format="DD/MM/YYYY HH:mm" value-format="YYYY-MM-DD HH:mm"
                                    :picker-options="{ firstDayOfWeek: 1 }" />
                            </div>
                        </div>
                        <div class="col-md-5 el-col-xs-24">
                            <div class="actions-container complete-return-actions">
                                <button type="button" class="btn btn-block btn_red complete-return-button"
                                    @click="completeReturnBySystems">
                                    Registrar devolución
                                </button>
                            </div>
                        </div>
                        <div class="col-12 mt-3">
                            <div class="form-item w-100 label-common pl-5">
                                <label for="systemReturnReason" class="w-100">Razón de devolución <span class="text-danger">*</span></label>
                                <el-input id="systemReturnReason" type="textarea" v-model="systemReturnReason"
                                    placeholder="Escribe la razón de devolución (obligatorio)" :rows="5" class="custom-textarea" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 mt-3" v-if="canReceiveReturn">
                <div>
                    <h3>Confirmar devolución</h3>
                    <div class="row f-flex">
                        <div class="col-md-7 el-col-xs-24">
                            <div class="form-item date-picker w-100 label-common pl-5">
                                <label for="freturn" class="w-100">Fecha de recepción de la devolución</label>
                                <el-date-picker popper-class="material-modal-picker" id="freturn" v-model="returnedSystemMaterial" name="from-dat'"
                                    class="w-100" type="datetime" placeholder="Fecha recepción"
                                    format="DD/MM/YYYY HH:mm" value-format="YYYY-MM-DD HH:mm"
                                    :picker-options="{ firstDayOfWeek: 1 }" />
                            </div>
                        </div>
                        <div class="col-md-5 el-col-xs-24">
                            <div class="actions-container complete-return-actions">
                                <button type="button" class="btn btn-block btn_yellow complete-return-button"
                                    @click="receiveMaterial">
                                    Confirmar devolución
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-12 mt-3" v-if="newMaterial.returnReason">
                <div class="form-item w-100 label-common pl-5 return-reason-field">
                    <label class="w-100">Razón de devolución</label>
                    <el-input type="textarea" v-model="newMaterial.returnReason" :rows="5" class="custom-textarea"
                        disabled />
                </div>
            </div>
        </div>
        <!--  Botones guardar y cancelar -->
        <div class="row footer">
            <div class="col-md-12 mt-4" style="margin-left: -10px;">
                <div class="d-flex justify-content-end gap-3">
                    <div class="col-md-3">
                        <button class="btn btn-block btn_yellow edit-button" @click="closeDialog()" style="">Cancelar
                        </button>
                    </div>
                    <div class="col-md-4" v-if="canConfirmDelivery">
                        <button type="button" class="btn btn-block btn_yellow edit-button"
                            @click="openEmployeeAction('confirm')">
                            Confirmar entrega
                        </button>
                    </div>
                    <div class="col-md-3" v-if="canRequestReturn">
                        <button type="button" class="btn btn-block btn_red edit-button"
                            @click="openEmployeeAction('return')">
                            Devolver
                        </button>
                    </div>
                    <div class="col-md-3" v-if="!employee">
                        <button type="submit" class="btn btn-block btn_red edit-button" @click="saveEntry">
                            Guardar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.header {
    svg {
        width: 30px;
        margin-bottom: 14px;
        fill: #474CE6;
    }
}

.header-title {
    font-size: 30px;
    margin-left: 5px;
    font-weight: 800;
    letter-spacing: -0.05em;
    color: #474ce6;
}

.custom-textarea:deep(.el-textarea__inner) {
    font-size: 14px !important;
    border-radius: 20px;
}

:deep(.el-select),
:deep(.el-select__wrapper) {
    width: 100%;
}

.material-selector-group,
.material-selector-row {
    width: 100%;
}

.actions-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    /* Empuja el contenido hacia abajo */
    align-items: flex-end;
    /* Lo alinea a la izquierda */
    height: 100%;
    padding: 1rem;
    /* Ajusta según necesites */
    margin-top: 7px;
}

.complete-return-actions {
    margin-top: 0;
}

.complete-return-button {
    width: 200px;
    margin-left: 0;
    margin-right: 0;
    box-sizing: border-box;
}

@media(max-width: 767.98px) {
    .header {
        align-items: center;
        display: flex;
    }

    .header-title {
        font-size: 24px;
        line-height: 1;
        margin-left: 0;
    }

    .row {
        margin-left: 0;
        margin-right: 0;
    }

    [class*="col-"] {
        flex: 0 0 100% !important;
        max-width: 100% !important;
        padding-left: 0;
        padding-right: 0;
        width: 100% !important;
    }

    .form-item {
        margin-top: 0.75rem !important;
        padding-left: 0 !important;
    }

    .material-selector-group,
    .material-selector-row {
        --bs-gutter-x: 0;
        margin-left: 0 !important;
        margin-right: 0 !important;
        max-width: 100% !important;
        width: 100% !important;
    }

    .material-selector-row > [class*="col-"] {
        flex: 0 0 100% !important;
        max-width: 100% !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        width: 100% !important;
    }

    .material-selector-row .form-item,
    .material-selector-row .el-select,
    .material-selector-row :deep(.el-select__wrapper) {
        max-width: 100% !important;
        width: 100% !important;
    }

    .el-select,
    .el-input,
    .el-date-editor {
        width: 100% !important;
        max-width: 100% !important;
    }

    :deep(.el-select__wrapper),
    :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box;
    }

    .actions-container {
        justify-content: center;
        align-items: center;
        padding: 0;
        width: 100%;

        button {
            width: 100% !important;
        }
    }

    .complete-return-button {
        width: 100% !important;
        max-width: 100% !important;
    }

    .complete-return-actions {
        margin-top: 7px;
    }

    .footer {
        width: 100%;

        .col-md-12 {
            margin-left: 0 !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            width: 100% !important;
        }

        .d-flex {
            align-items: stretch;
            flex-direction: column;
            gap: 0.75rem !important;
            padding: 0 4px;
            width: 100%;
        }

        .col-md-3,
        .col-md-4 {
            flex: 0 0 100% !important;
            max-width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
            width: 100%;
        }

        .edit-button {
            box-sizing: border-box;
            min-width: 0;
            width: 100%;
        }
    }
}

@media (max-width: 500px) {
    .header-title {
        font-size: 24px;
    }
}
</style>
